// ABOUTME: Composición por máscara del modo foto: del resultado generado solo se toma la zona
// ABOUTME: de pelo (nuevo y viejo); cara, ropa y fondo son los píxeles de la selfie original.
import { HEAD_CLASSES } from "../calibration/silhouette";
import { drawWatermark, pickWatermarkCorner } from "./watermark";

/** 255 donde cualquiera de las dos máscaras de categorías marca pelo. */
export function unionHairMask(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length);
  for (let i = 0; i < a.length; i++) out[i] = a[i] === HEAD_CLASSES.hair || b[i] === HEAD_CLASSES.hair ? 255 : 0;
  return out;
}

/**
 * Para selfies con aire añadido: se conserva de la original solo la piel de cara y cuello
 * (erosionada `erode` px para que el borde lo pinte la generada); todo lo demás es generado.
 */
export function keepFaceAlpha(mask: Uint8Array, width: number, height: number, erode: number): Uint8Array {
  const skin = new Uint8Array(mask.length);
  for (let i = 0; i < mask.length; i++) skin[i] = mask[i] === HEAD_CLASSES.faceSkin || mask[i] === BODY_SKIN ? 255 : 0;
  // Erosión = dilatación del complemento.
  const inverse = new Uint8Array(skin.length);
  for (let i = 0; i < skin.length; i++) inverse[i] = skin[i] ? 0 : 255;
  return dilate(inverse, width, height, erode);
}

const BODY_SKIN = 2;

/** Dilatación en cruz (4-vecinos) repetida `radius` veces. */
export function dilate(mask: Uint8Array, width: number, height: number, radius: number): Uint8Array {
  let current = mask;
  for (let r = 0; r < radius; r++) {
    const next = new Uint8Array(current);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (!current[y * width + x]) continue;
        if (x > 0) next[y * width + x - 1] = 255;
        if (x < width - 1) next[y * width + x + 1] = 255;
        if (y > 0) next[(y - 1) * width + x] = 255;
        if (y < height - 1) next[(y + 1) * width + x] = 255;
      }
    }
    current = next;
  }
  return current;
}

/** out = generated·alpha + original·(1−alpha), por píxel RGBA; alpha en 0..255 por píxel. */
export function blendByAlpha(
  original: Uint8ClampedArray,
  generated: Uint8ClampedArray,
  alpha: Uint8ClampedArray,
): Uint8ClampedArray<ArrayBuffer> {
  const out = new Uint8ClampedArray(new ArrayBuffer(original.length));
  for (let p = 0; p < alpha.length; p++) {
    const a = alpha[p] / 255;
    const o = p * 4;
    for (let c = 0; c < 3; c++) out[o + c] = generated[o + c] * a + original[o + c] * (1 - a);
    out[o + 3] = 255;
  }
  return out;
}

export interface Segmenter {
  segment(source: CanvasImageSource, timestampMs: number): { data: Uint8Array; width: number; height: number };
}

async function toCanvas(src: Blob | string, width: number, height: number): Promise<HTMLCanvasElement> {
  const bitmap = await createImageBitmap(typeof src === "string" ? await (await fetch(src)).blob() : src);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return canvas;
}

/**
 * Compone la foto final: segmenta pelo en la selfie y en la generada, une, dilata unos px y
 * suaviza el borde escalando la máscara; mezcla por alpha. Devuelve un JPEG.
 */
export async function composeHairOnly(
  originalDataUrl: string,
  generated: Blob,
  segmenter: Segmenter,
  options: { watermark?: boolean; label?: string; qrUrl?: string } = {},
): Promise<{ blob: Blob; clean: Blob }> {
  const probe = await createImageBitmap(await (await fetch(originalDataUrl)).blob());
  const width = probe.width;
  const height = probe.height;
  probe.close();
  const original = await toCanvas(originalDataUrl, width, height);
  const result = await toCanvas(generated, width, height);

  const mo = segmenter.segment(original, performance.now());
  const mg = segmenter.segment(result, performance.now());
  if (mo.width !== mg.width || mo.height !== mg.height) throw new Error("Máscaras de tamaño distinto");
  const radius = Math.max(2, Math.round(mo.width * 0.02));
  const hair = dilate(unionHairMask(mo.data, mg.data), mo.width, mo.height, radius);

  // Máscara pequeña → canvas → escalada con suavizado = borde difuminado gratis.
  const small = document.createElement("canvas");
  small.width = mo.width;
  small.height = mo.height;
  const smallData = small.getContext("2d")!.createImageData(mo.width, mo.height);
  for (let i = 0; i < hair.length; i++) smallData.data.set([hair[i], hair[i], hair[i], 255], i * 4);
  small.getContext("2d")!.putImageData(smallData, 0, 0);
  const big = document.createElement("canvas");
  big.width = width;
  big.height = height;
  const bctx = big.getContext("2d")!;
  bctx.imageSmoothingEnabled = true;
  bctx.imageSmoothingQuality = "high";
  // Desenfoque del borde (≈1,5 % del ancho) para que no se vean costuras entre capas.
  bctx.filter = `blur(${Math.max(3, Math.round(width * 0.015))}px)`;
  bctx.drawImage(small, 0, 0, width, height);
  bctx.filter = "none";
  const alphaRgba = bctx.getImageData(0, 0, width, height).data;
  const alpha = new Uint8ClampedArray(width * height);
  for (let p = 0; p < alpha.length; p++) alpha[p] = alphaRgba[p * 4];
  const oData = original.getContext("2d")!.getImageData(0, 0, width, height).data;
  const gData = result.getContext("2d")!.getImageData(0, 0, width, height).data;
  const out = new ImageData(blendByAlpha(oData, gData, alpha), width, height);
  const outCanvas = document.createElement("canvas");
  outCanvas.width = width;
  outCanvas.height = height;
  const octx = outCanvas.getContext("2d")!;
  octx.putImageData(out, 0, 0);
  const toBlob = (): Promise<Blob> =>
    new Promise((resolve, reject) => outCanvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob falló"))), "image/jpeg", 0.92));
  // Versión limpia (sin marca) para poder pedir otras vistas a partir de ella.
  const clean = await toBlob();
  if (options.watermark) await drawWatermark(octx, width, height, pickWatermarkCorner(hair, mo.width, mo.height), options.label, options.qrUrl);
  return { blob: await toBlob(), clean };
}

/** Sin segmentador: marca de agua abajo a la derecha sobre la imagen generada. */
export async function watermarkOnly(generated: Blob, label?: string, qrUrl?: string): Promise<Blob> {
  const bitmap = await createImageBitmap(generated);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  await drawWatermark(ctx, canvas.width, canvas.height, "bottom-right", label, qrUrl);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob falló"))), "image/jpeg", 0.92));
}
